import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCity } from "@/contexts/Citycontext";
import JobCard from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import MultiSelectDropdown from "@/components/MultiSelectDropdown";
import RangeSlider from "@/components/RangeSlider";
import SegmentedToggle from "@/components/SegmentedToggle";
import { EDUCATION_LEVELS, FRESHNESS_OPTIONS } from "@/constants/education";
import PwaPromoBanner from "@/components/PwaPromoBanner";
import { Search, SlidersHorizontal, MapPin, X, GraduationCap, Clock, Zap } from "lucide-react";

const TYPES = ["Full-time","Part-time","Daily Wage","Contract","Internship","Walk-in"];
const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "salary_high", label: "Salary: high to low" },
  { value: "most_applied", label: "Most applied" },
];

const SALARY_CAPS = {
  yearly:  { min: 0, max: 30,     step: 0.5 },
  monthly: { min: 0, max: 100000, step: 1000 },
};
const EXP_CAP = { min: 0, max: 20, step: 1 };

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [educationFacets, setEducationFacets] = useState(
    EDUCATION_LEVELS.map(l => ({ value: l, label: l, count: 0 }))
  );

  // Track whether this is the very first render so we
  // don't fight with the initial URL that may already have a city
  const isFirstRender = useRef(true);

  const { selectedCity, setSelectedCity } = useCity();

  const q          = params.get("q") || "";
  const cityParam  = params.get("city") || "";
  const category   = params.get("category") || "";
  const job_type   = params.get("job_type") || "";
  const sort       = params.get("sort") || "newest";
  const page       = parseInt(params.get("page") || "1", 10);
  const educationParam = params.get("education") || "";
  const freshnessParam = params.get("freshness") || "";
  const urgencyParam   = params.get("urgency") || "";
  const salaryMode      = params.get("salary_mode") || "yearly";
  const salaryLoParam   = params.get("salary_lo");
  const salaryHiParam   = params.get("salary_hi");
  const hideUndisclosedSalary = params.get("hide_undisclosed_salary") === "1";
  const expLoParam   = params.get("exp_lo");
  const expHiParam   = params.get("exp_hi");
  const hideUndisclosedExp = params.get("hide_undisclosed_exp") === "1";

  const cityValues      = cityParam ? cityParam.split(",").filter(Boolean) : [];
  const educationValues = educationParam ? educationParam.split(",").filter(Boolean) : [];
  const freshnessValues = freshnessParam ? freshnessParam.split(",").filter(Boolean) : [];

  const salaryTouched = salaryLoParam !== null || salaryHiParam !== null;
  const salaryCap = SALARY_CAPS[salaryMode] || SALARY_CAPS.yearly;
  const salaryRangeValue = [
    salaryLoParam !== null ? Number(salaryLoParam) : salaryCap.min,
    salaryHiParam !== null ? Number(salaryHiParam) : salaryCap.max,
  ];

  const expTouched = expLoParam !== null || expHiParam !== null;
  const expRangeValue = [
    expLoParam !== null ? Number(expLoParam) : 0,
    expHiParam !== null ? Number(expHiParam) : 3,
  ];

  // ── SYNC 1: On first mount, inject selectedCity into URL if URL has no city ──
  useEffect(() => {
    if (selectedCity && !params.get("city")) {
      const np = new URLSearchParams(params);
      np.set("city", selectedCity);
      setParams(np, { replace: true });
    }
    // mark first render done AFTER the check
    isFirstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── SYNC 2: When navbar city changes AFTER mount, update URL param live ──
  useEffect(() => {
    // Skip on first render — handled by SYNC 1 above
    if (isFirstRender.current) return;

    const np = new URLSearchParams(params);
    if (selectedCity) {
      np.set("city", selectedCity);
    } else {
      np.delete("city");
    }
    np.delete("page"); // reset to page 1 on city change
    setParams(np, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    api.get("/cities").then(r => setCities(r.data));
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/jobs/education-facets").then(r => setEducationFacets(r.data)).catch(() => {});
  }, []);

  // ── Fetch jobs whenever any filter/URL param changes ──
  useEffect(() => {
    setLoading(true);
    const query = { sort, page, limit: 12 };
    if (q) query.q = q;
    if (cityValues.length) query.city = cityValues.join(",");
    if (category) query.category = category;
    if (job_type) query.job_type = job_type;
    if (educationValues.length) query.education = educationValues.join(",");
    if (freshnessValues.length) query.freshness_days = Math.max(...freshnessValues.map(Number));
    if (urgencyParam) query.urgency = urgencyParam;
    if (salaryTouched) {
      query.salary_mode = salaryMode;
      query.salary_range_min = salaryRangeValue[0];
      query.salary_range_max = salaryRangeValue[1];
      if (hideUndisclosedSalary) query.hide_undisclosed_salary = true;
    }
    if (expTouched) {
      query.experience_range_min = expRangeValue[0];
      query.experience_range_max = expRangeValue[1];
      if (hideUndisclosedExp) query.hide_undisclosed_experience = true;
    }

    api.get("/jobs", { params: query })
      .then(r => {
        setJobs(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));

    if (q || cityValues.length || category) {
      const wider = { sort: "newest", page: 1, limit: 6 };
      if (cityValues.length) wider.city = cityValues.join(",");
      else if (category) wider.category = category;
      api.get("/jobs", { params: wider }).then(r => setSimilar(r.data.items));
    } else {
      setSimilar([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    q, cityParam, category, job_type, sort, page,
    educationParam, freshnessParam, urgencyParam,
    salaryMode, salaryLoParam, salaryHiParam, hideUndisclosedSalary,
    expLoParam, expHiParam, hideUndisclosedExp,
  ]);

  // Update a single-value URL param + keep context in sync
  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (!v || v === "all") np.delete(k); else np.set(k, v);
    np.delete("page");
    setParams(np);
    if (k === "city") setSelectedCity(v === "all" ? "" : v);
  };

  // Update a multi-value (comma-joined) URL param
  const setMultiParam = (k, arr) => {
    const np = new URLSearchParams(params);
    if (!arr || arr.length === 0) np.delete(k); else np.set(k, arr.join(","));
    np.delete("page");
    setParams(np);
  };

  const setCityValues = (arr) => {
    setMultiParam("city", arr);
    setSelectedCity(arr.length === 1 ? arr[0] : "");
  };

  const setBoolParam = (k, val) => {
    const np = new URLSearchParams(params);
    if (val) np.set(k, "1"); else np.delete(k);
    np.delete("page");
    setParams(np);
  };

  const setSalaryRange = ([lo, hi]) => {
    const np = new URLSearchParams(params);
    np.set("salary_lo", String(lo));
    np.set("salary_hi", String(hi));
    np.delete("page");
    setParams(np);
  };

  const setSalaryMode = (mode) => {
    const np = new URLSearchParams(params);
    np.set("salary_mode", mode);
    // scale is different per mode — drop any touched range so it doesn't carry over
    np.delete("salary_lo");
    np.delete("salary_hi");
    np.delete("page");
    setParams(np);
  };

  const setExpRange = ([lo, hi]) => {
    const np = new URLSearchParams(params);
    np.set("exp_lo", String(lo));
    np.set("exp_hi", String(hi));
    np.delete("page");
    setParams(np);
  };

  const clearAll = () => {
    setParams(new URLSearchParams());
    setSelectedCity("");
  };

  const cityLabel = cityValues.length === 0
    ? null
    : cityValues.length <= 2
      ? cityValues.join(", ")
      : `${cityValues.length} cities`;

  const activeFilterCount = [
    q, category, job_type, urgencyParam,
    cityValues.length ? "1" : "",
    educationValues.length ? "1" : "",
    freshnessValues.length ? "1" : "",
    salaryTouched ? "1" : "",
    expTouched ? "1" : "",
  ].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="jobs-page">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            {loading ? "Searching..." : `${total} jobs found`}
          </h1>
          <p className="text-slate-600 text-sm mt-0.5">
            {cityLabel
              ? <>Showing results in <span className="font-semibold text-slate-900">{cityLabel}</span> — refine with the filters below.</>
              : "Refine your search with the filters below."
            }
          </p>
        </div>
        <Select value={sort} onValueChange={v => setParam("sort", v)}>
          <SelectTrigger data-testid="sort-select" className="w-56">
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* ── Active filter badges ── */}
      {activeFilterCount > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {cityValues.map(c => (
            <div key={c} className="flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-full text-xs font-medium">
              <MapPin className="w-3.5 h-3.5" />
              {c}
              <button type="button" onClick={() => setCityValues(cityValues.filter(x => x !== c))}
                className="ml-1 hover:opacity-70" data-testid="remove-city-filter">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {category && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white rounded-full text-xs font-medium">
              {category}
              <button type="button" onClick={() => setParam("category", "all")} className="ml-1 hover:opacity-70">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {job_type && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white rounded-full text-xs font-medium">
              {job_type}
              <button type="button" onClick={() => setParam("job_type", "all")} className="ml-1 hover:opacity-70">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {q && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white rounded-full text-xs font-medium">
              "{q}"
              <button type="button" onClick={() => setParam("q", "")} className="ml-1 hover:opacity-70">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <button type="button" onClick={clearAll}
            className="text-xs text-slate-500 hover:text-black underline ml-1">
            Clear all
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">

        {/* ── Filters sidebar ── */}
        <aside className="lg:col-span-1 space-y-4 lg:sticky lg:top-20 lg:self-start">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Filters</h3>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearAll}
                  className="text-xs text-slate-500 hover:text-black flex items-center gap-1"
                  data-testid="clear-filters">
                  <X className="w-3 h-3" /> Clear all
                </button>
              )}
            </div>

            {/* Keyword */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Keyword</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black" />
                <Input
                  data-testid="filter-keyword"
                  defaultValue={q}
                  onKeyDown={e => { if (e.key === "Enter") setParam("q", e.target.value); }}
                  placeholder="Search..."
                  className="pl-9"
                />
              </div>
            </div>

            {/* City — multi-select, controlled by both sidebar & navbar context */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">City</label>
              <MultiSelectDropdown
                data-testid="filter-city"
                icon={MapPin}
                iconClassName="text-black"
                values={cityValues}
                onChange={setCityValues}
                placeholder="All cities"
                searchPlaceholder="Search city..."
                options={cities.map(c => ({ value: c.name, label: c.name, count: c.job_count }))}
                triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
              />
              {/* Hint when city came from navbar */}
              {cityValues.length === 1 && selectedCity === cityValues[0] && (
                <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5" /> Set from navbar city filter
                </p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
              <SearchableSelect
                data-testid="filter-category"
                value={category || "all"}
                onChange={v => setParam("category", v)}
                clearValue="all"
                clearLabel="All categories"
                searchPlaceholder="Search category..."
                options={categories.map(c => ({ value: c.name, label: c.name }))}
                triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
              />
            </div>

            {/* Job Type */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Job Type</label>
              <Select value={job_type || "all"} onValueChange={v => setParam("job_type", v)}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Salary range */}
            <div className="pt-1 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2.5 mt-3">
                <label className="text-xs font-medium text-slate-600">Salary</label>
                <SegmentedToggle
                  options={[{ value: "yearly", label: "LPA" }, { value: "monthly", label: "Monthly" }]}
                  value={salaryMode}
                  onChange={setSalaryMode}
                  data-testid="salary-mode-toggle"
                />
              </div>
              <RangeSlider
                min={salaryCap.min}
                max={salaryCap.max}
                step={salaryCap.step}
                value={salaryRangeValue}
                onChange={setSalaryRange}
                formatLabel={v => salaryMode === "yearly" ? `${v} LPA` : `₹${Number(v).toLocaleString()}`}
              />
              <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideUndisclosedSalary}
                  onChange={e => setBoolParam("hide_undisclosed_salary", e.target.checked)}
                  style={{ accentColor: "#000" }}
                  className="w-3.5 h-3.5 rounded border-slate-300"
                />
                Don't show undisclosed salary
              </label>
            </div>

            {/* Experience range */}
            <div className="pt-1 border-t border-slate-100">
              <label className="text-xs font-medium text-slate-600 mb-2.5 block mt-3">Experience</label>
              <RangeSlider
                min={EXP_CAP.min}
                max={EXP_CAP.max}
                step={EXP_CAP.step}
                value={expRangeValue}
                onChange={setExpRange}
                formatLabel={v => `${v} yr${v === 1 ? "" : "s"}`}
              />
              <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideUndisclosedExp}
                  onChange={e => setBoolParam("hide_undisclosed_exp", e.target.checked)}
                  style={{ accentColor: "#000" }}
                  className="w-3.5 h-3.5 rounded border-slate-300"
                />
                Don't show undisclosed experience
              </label>
            </div>

            {/* Minimum education */}
            <div className="pt-1 border-t border-slate-100">
              <label className="text-xs font-medium text-slate-600 mb-1 block mt-3">Minimum Education</label>
              <MultiSelectDropdown
                icon={GraduationCap}
                iconClassName="text-black"
                values={educationValues}
                onChange={arr => setMultiParam("education", arr)}
                placeholder="Any education"
                searchPlaceholder="Search education..."
                options={educationFacets}
                triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
              />
            </div>

            {/* Freshness */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Date Posted</label>
              <MultiSelectDropdown
                icon={Clock}
                iconClassName="text-black"
                values={freshnessValues}
                onChange={arr => setMultiParam("freshness", arr)}
                placeholder="Any time"
                showSearch={false}
                options={FRESHNESS_OPTIONS}
              triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
              />
            </div>

            {/* Urgent / Immediate hiring */}
            <div className="pt-1 border-t border-slate-100">
              <label className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1 mt-3">
                <Zap className="w-3.5 h-3.5" /> Hiring urgency
              </label>
              <SegmentedToggle
                options={[
                  { value: "urgent", label: "Urgent Hiring" },
                  { value: "immediate", label: "Immediate Hiring" },
                ]}
                value={urgencyParam}
                onChange={v => setParam("urgency", v)}
                allowDeselect
              />
            </div>

            <Button className="w-full bg-black hover:bg-slate-800 text-white" onClick={clearAll} data-testid="clear-filters-btn">
              Clear filters
            </Button>
          </div>
        </aside>

        {/* ── Job grid ── */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton h-44 rounded-xl" />
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center p-12 bg-white border border-slate-200 rounded-xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/06e938ed-169f-4658-9655-68b603410b7a/images/84c020bf88e72ddbd32801670eef3ba776a24b315e6a509daf9c4fe9117267b9.png"
                className="w-40 h-40" alt=""
              />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No jobs found</h3>
              <p className="text-slate-500 mb-6">
                {cityLabel
                  ? `No jobs found in ${cityLabel}. Try clearing the city filter or search all cities.`
                  : "Try clearing filters or searching for something else."
                }
              </p>
              <Button onClick={clearAll} className="bg-black hover:bg-slate-800 text-white">
                Clear filters
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {jobs.map((j, i) => (
                  <React.Fragment key={j.id}>
                    <JobCard job={j} />
                    {i === 9 && (
                      <div className="md:col-span-2">
                        <PwaPromoBanner />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex justify-center gap-2 mt-8">
                <Button variant="outline" disabled={page <= 1}
                  onClick={() => setParam("page", String(page - 1))} data-testid="prev-page">
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm text-slate-600">Page {page}</span>
                <Button variant="outline" disabled={page * 12 >= total}
                  onClick={() => setParam("page", String(page + 1))} data-testid="next-page">
                  Next
                </Button>
              </div>
            </>
          )}

          {/* Similar jobs */}
          {similar.length > 0 && (q || cityValues.length > 0 || category) && (
            <section className="mt-12 pt-8 border-t border-slate-200" data-testid="similar-jobs-section">
              <h2 className="text-xl font-bold text-slate-900 mb-1">You may also like</h2>
              <p className="text-sm text-slate-500 mb-5">Similar jobs based on your search.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {similar.filter(s => !jobs.find(j => j.id === s.id)).slice(0, 4).map(j => (
                  <JobCard key={j.id} job={j} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
